import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useSponsorshipTiers } from "@/hooks/useSponsorshipTiers";
import { supabase } from "@/integrations/supabase/client";
import { Send, Building2 } from "lucide-react";

interface FormData {
  company: string;
  contact_name: string;
  email: string;
  phone: string;
  tier_id: string;
  amount: string;
  message: string;
}

export const SponsorForm = () => {
  const { tiers } = useSponsorshipTiers();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState<FormData>({
    company: "",
    contact_name: "",
    email: "",
    phone: "",
    tier_id: "",
    amount: "",
    message: ""
  });

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getUtmData = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      source: urlParams.get('utm_source') || 'direct',
      medium: urlParams.get('utm_medium') || 'website',
      campaign: urlParams.get('utm_campaign') || 'cncf-sponsor',
      term: urlParams.get('utm_term') || null,
      content: urlParams.get('utm_content') || null,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.company || !formData.contact_name || !formData.email) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.functions.invoke('sponsor-inquiry', {
        body: {
          company: formData.company,
          contact_name: formData.contact_name,
          email: formData.email,
          phone: formData.phone || null,
          tier_id: formData.tier_id || null,
          amount: formData.amount ? parseFloat(formData.amount) : null,
          message: formData.message || null,
          utm_data: getUtmData()
        }
      });

      if (error) throw error;

      toast({
        title: "Inquiry Submitted!",
        description: "Thank you for your interest. We'll be in touch within 1-2 business days.",
      });

      // Reset form
      setFormData({
        company: "",
        contact_name: "",
        email: "",
        phone: "",
        tier_id: "",
        amount: "",
        message: ""
      });

    } catch (error: any) {
      console.error('Error submitting sponsor inquiry:', error);
      toast({
        title: "Submission Error",
        description: "Please try again or contact us directly at contact@boboandteedee.com",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedTier = tiers.find(t => t.id === formData.tier_id);

  return (
    <section id="sponsor-form" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <Building2 className="h-16 w-16 mx-auto mb-6 text-primary" />
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Get in Touch
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Ready to partner with us? Fill out the form below and we'll contact you 
            within 1-2 business days to discuss your sponsorship opportunity.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="shadow-lg">
            <CardHeader className="text-center bg-gradient-to-r from-primary/10 to-secondary/10">
              <CardTitle className="text-2xl">Sponsorship Inquiry</CardTitle>
              <p className="text-muted-foreground">
                Let's discuss how we can work together
              </p>
            </CardHeader>
            
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="company">Company Name *</Label>
                    <Input
                      id="company"
                      type="text"
                      value={formData.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      placeholder="Your organization"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="contact_name">Contact Name *</Label>
                    <Input
                      id="contact_name"
                      type="text"
                      value={formData.contact_name}
                      onChange={(e) => handleInputChange('contact_name', e.target.value)}
                      placeholder="Your full name"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="contact@company.com"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="+1 (345) 123-4567"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="tier-select">Sponsorship Tier</Label>
                    <Select onValueChange={(value) => handleInputChange('tier_id', value)}>
                      <SelectTrigger id="tier-select">
                        <SelectValue placeholder="Select a tier" />
                      </SelectTrigger>
                      <SelectContent>
                        {tiers.map((tier) => (
                          <SelectItem key={tier.id} value={tier.id}>
                            {tier.name} - {tier.currency} {tier.price_min?.toLocaleString()}
                            {tier.price_max && tier.price_max !== tier.price_min && 
                              ` - ${tier.price_max.toLocaleString()}`
                            }
                            {!tier.price_max && tier.level === 1 && '+'}
                          </SelectItem>
                        ))}
                        <SelectItem value="custom">Custom Amount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="amount">Proposed Amount (KYD)</Label>
                    <Input
                      id="amount"
                      type="number"
                      value={formData.amount}
                      onChange={(e) => handleInputChange('amount', e.target.value)}
                      placeholder={selectedTier ? `${selectedTier.price_min}` : "Enter amount"}
                      min="1000"
                      step="100"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder="Tell us about your organization and why you'd like to partner with Bobo & Teedee®..."
                    rows={4}
                  />
                </div>

                <div className="pt-4">
                  <Button 
                    type="submit" 
                    className="w-full text-lg py-6"
                    disabled={loading}
                  >
                    {loading ? (
                      "Submitting..."
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Submit Sponsorship Inquiry
                      </>
                    )}
                  </Button>
                </div>

                <div className="text-center text-sm text-muted-foreground">
                  <p>
                    By submitting this form, you agree to be contacted by our team regarding 
                    sponsorship opportunities. We respect your privacy and will not share your information.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};